'use client';
import { forwardRef, useState, useRef, useEffect } from 'react';
import { Box, Tab, useMediaQuery } from '@mui/material';
import { TabContext, TabList } from '@mui/lab';
import { useTheme } from '@mui/material/styles';

import { FadedEdgesContainer } from '../FadedEdgesContainer';
import { NerdWalletThemeProvider } from '../NerdWalletThemeProvider';
import { getThemeOverrides } from './theme';
import Link from 'next/link';
import { QuickFiltersVariant } from './config';

type GrayDeselectableQuickFiltersProps = {
  filtersList: string[];
  hrefMap?: { [k: string]: string };
  preselectedIndex?: number;
  onSelectTab: (index: number | null) => void;
  disabled?: boolean;
  variant?: QuickFiltersVariant;
};

export const GrayDeselectableQuickFilters = forwardRef<
  HTMLDivElement,
  GrayDeselectableQuickFiltersProps
>(
  (
    {
      disabled = false,
      filtersList,
      hrefMap,
      onSelectTab,
      preselectedIndex,
      variant = QuickFiltersVariant.Default,
    },
    ref,
  ) => {
    const theme = useTheme();
    const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);

    const localRef = useRef<Element | null | undefined>(null);
    const tabButtonRefs = useRef<Array<HTMLButtonElement | HTMLAnchorElement | null>>([]);

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleChange = (index: number) => {
      if (index !== selectedTabIndex) {
        setSelectedTabIndex(index);
        onSelectTab(index);
      }
    };

    useEffect(() => {
      setSelectedTabIndex(preselectedIndex ?? 0);
    }, []);

    return (
      <TabContext value={selectedTabIndex?.toString() || '0'}>
        <NerdWalletThemeProvider merge={getThemeOverrides(theme, variant)}>
          <Box
            ref={ref}
            sx={{
              position: 'relative',
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'center' },
              py: 2,
              overflowX: 'auto',
            }}
          >
            <FadedEdgesContainer
              Component={TabList}
              showStartFadeByDefault
              showEndFadeByDefault={true}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons={false}
              fadeSize="15%"
              visibleScrollbar={false}
              allowScrollButtonsMobile
              aria-label="Product offers quick filters"
              sx={{
                display: 'flex',
                px: 3.5,
                maxWidth: { md: 'none' },
                '.MuiTabs-flexContainer': {
                  justifyContent: 'flex-start',
                  flexWrap: 'nowrap',
                },
              }}
              overrideRef={localRef}
            >
              {filtersList.map((filter, index) => (
                <Tab
                  label={filter}
                  value={index.toString()}
                  disabled={disabled}
                  key={filter}
                  component={hrefMap ? Link : 'button'}
                  href={hrefMap?.[filter]}
                  ref={(ref: HTMLButtonElement | HTMLAnchorElement | null) => {
                    tabButtonRefs.current[index] = ref;
                    if (index === 0) {
                      const element = ref?.closest('[role="tablist"]')?.parentElement;
                      localRef.current = element;
                    }
                  }}
                  onClick={() => handleChange(index)}
                />
              ))}
            </FadedEdgesContainer>
          </Box>
        </NerdWalletThemeProvider>
      </TabContext>
    );
  },
);
